import React from 'react';
import { Order, OrderStatus } from '@/domain/entities';

interface OrdersTableProps {
  orders: Order[];
  onViewDetails: (order: Order) => void;
}

const STATUSES: { value: OrderStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Ожидает', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  { value: 'confirmed', label: 'Подтвержден', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { value: 'shipped', label: 'Отгружен', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  { value: 'delivered', label: 'Доставлен', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  { value: 'cancelled', label: 'Отменён', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
];

export const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  onViewDetails,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = STATUSES.find(s => s.value === status);
    if (!statusConfig) return null;

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow ring-1 ring-black ring-opacity-5 dark:ring-gray-700 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
        Заказы не найдены
      </div>
    );
  }

  const isCompletedOrder = (status: OrderStatus) => {
    return status === 'delivered' || status === 'cancelled';
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Номер
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Покупатель
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Контакт
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Статус
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Сумма
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Товаров
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Дата
              </th>
              <th className="py-2.5 px-3 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-300 dark:border-gray-600">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {orders.map((order) => {
              const isCompleted = isCompletedOrder(order.status);
              return (
              <tr 
                key={order.id} 
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 ${
                  isCompleted ? 'opacity-60 bg-gray-50/50 dark:bg-gray-900/30' : ''
                }`}
              >
                <td className="whitespace-nowrap py-2.5 px-4 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {order.orderNumber}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {order.customerName}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {order.customerContactValue}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm border-r border-gray-200 dark:border-gray-700">
                  {getStatusBadge(order.status)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {formatCurrency(order.totalPrice)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {order.itemsCount}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {formatDate(order.createdAt)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewDetails(order)}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                    >
                      Просмотр
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

